import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { IconV3Component } from '@gea/digital-ui-lib';
import { FieldArrayType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormFieldsService } from '../app/services/form-fields.service';
import { TabGroupComponent } from 'src/app/shared/components/tab-group/tab-group.component';
import { TabItem } from 'src/app/shared/models/formly-custom';
import { buildGridContainerClasses } from 'src/app/services/FormlyHelper';

@Component({
  selector: 'formly-dynamical-tab-list',
  imports: [ReactiveFormsModule, FormlyModule, TabGroupComponent, IconV3Component],
  template: `
    <div class="tabs-header">
      <app-tab-group [tabList]="tabItems" [activeItem]="activeTabItem" (tabChanged)="onTabChange($event)"></app-tab-group>
      @if (tabItems.length < MAX_TABS_COUNT && !formControl.disabled && !formlyService.isCollectionOpPending()) {
        <geaui-icon-v3
          [color]="'blue'"
          [hoverColor]="'grey'"
          [file]="'16px_e-add'"
          (click)="addTab(true)"
          style="cursor: pointer; padding-left: 1rem;">
        </geaui-icon-v3>
      }
    </div>
    @if (props.label) {
      <div class="condition-heading">
        <span class="gea-font-heading-20">{{ props.label }}</span>
        @if (canDeleteActiveTab) {
          <geaui-icon-v3
            class="condition-delete-btn"
            [color]="'var(--geaui-tertiary-red-70, #db1e28)'"
            [file]="'16px_remove'"
            (click)="deleteActiveTab()"></geaui-icon-v3>
        }
      </div>
    }
    <div class="tab-content">
      @for (tabDefinition of tabFieldGroups; let tabIndex = $index; track tabDefinition) {
        @if (activeIndex === tabIndex) {
          <div [class]="gridContainerClasses">
            @for (field of tabDefinition.fieldGroup; let fieldIndex = $index; track field) {
              <formly-field [field]="field"></formly-field>
            }
          </div>
        }
      }
    </div>
  `,
  styles: [
    `
      .tabs-header {
        display: flex;
        align-items: center;
        margin-bottom: 1.6rem;
      }
      .tabs-header button {
        margin-left: 1rem;
      }
      .condition-heading {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1rem;
      }
      .condition-delete-btn {
        cursor: pointer;
      }
      .tab-content {
        padding: 0;
      }

      .add-btn {
        cursor: pointer;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicalTabListComponent extends FieldArrayType implements OnInit {
  activeIndex = 0;
  tabItems: TabItem[] = [];
  defaultTabItem: TabItem = { id: 0, label: 'Condition 1', opId: '' };
  activeTabItem: TabItem = this.defaultTabItem;
  fromUI = false;
  readonly MAX_TABS_COUNT = 8;
  readonly FIRST_TAB_INDEX = 0;

  /** Grid classes for the per-tab field area, derived from this list's own layout props. */
  get gridContainerClasses(): string {
    return buildGridContainerClasses(this.props);
  }

  /** The first tab (operation point key 0) is relied upon by the rules and must never be deleted. */
  get canDeleteActiveTab(): boolean {
    return (
      this.activeIndex !== this.FIRST_TAB_INDEX &&
      this.tabItems.length > 1 &&
      !this.formControl.disabled &&
      !this.formlyService.isCollectionOpPending()
    );
  }
  constructor(
    private cd: ChangeDetectorRef,
    protected readonly formlyService: FormFieldsService
  ) {
    super();
    effect(() => {
      // component re-rendered on ngOnInit, no need to call the model
      const tabOperationResp = this.formlyService.tabOperationResp();
      const fieldArray = this.getFieldArray();
      const tabDefinitions = (fieldArray as any)?.fieldGroup || [];
      if (tabOperationResp != undefined) {
        switch (tabOperationResp.operation) {
          case 'addTab':
            // make last tab as active.
            this.activeIndex = this.formlyService.activeTabIndex = tabDefinitions.length - 1;
            this.updateActiveTabItem();
            break;
          case 'removeTab':
            // if its the last tab then make the second last one active.
            this.activeIndex = this.formlyService.activeTabIndex =
              this.formlyService.removedTabIndex == tabDefinitions.length
                ? this.formlyService.removedTabIndex - 1
                : this.formlyService.removedTabIndex;
            this.updateActiveTabItem();
            break;
        }
      }
    });
  }

  ngOnInit(): void {
    this.addTabDefinitions();
    this.updateTabItems();
    this.updateActiveTabItem();
  }

  private addTabDefinitions() {
    const fieldArray = this.getFieldArray();
    const tabDefinitions = (fieldArray as any)?.fieldGroup || [];

    if (tabDefinitions.length === 0) {
      // No field groups exist, add a default tab.
      this.add();
    } else {
      if (Array.isArray(this.model)) {
        this.model.length = 0;
      }
      super.add(); // Creates the model entry
      this.cd.markForCheck();
    }
  }

  getFieldGroup(): FormlyFieldConfig[] {
    return (this.field as any)?.fieldGroup || [];
  }

  getFieldArray(): FormlyFieldConfig {
    return (this.field as any)?.fieldArray || [];
  }

  get tabFieldGroups(): any[] {
    const fieldGroup = this.getFieldGroup();

    // The fieldGroup should contain the actual renderable field definitions
    if (Array.isArray(fieldGroup) && fieldGroup.length > 0 && fieldGroup[0].fieldGroup) {
      return fieldGroup[0].fieldGroup;
    }

    return [];
  }

  updateTabItems() {
    const fieldGroup = this.getFieldGroup();
    let tabDefinitions: any[] = [];

    if (fieldGroup && fieldGroup.length > 0 && fieldGroup[0].fieldGroup) {
      tabDefinitions = fieldGroup[0].fieldGroup;
    }

    this.tabItems = tabDefinitions.map((tab: any, index: number) => ({
      id: this.model && this.model[index]?.id !== undefined ? this.model[index].id : index,
      opId: tab.id,
      label: `Condition ${index + 1}`,
    }));

    this.cd.markForCheck();
  }

  updateActiveTabItem() {
    this.activeTabItem = this.tabItems[this.activeIndex] || this.defaultTabItem;
    this.cd.markForCheck();
  }

  onTabChange(item: TabItem) {
    const index = this.tabItems.findIndex((tab: any) => tab.opId === item.opId);
    if (index !== -1) {
      this.activeIndex = this.formlyService.activeTabIndex = index;
      this.updateActiveTabItem();
    }
  }

  addTab(fromUI = false): void {
    let newTabIndex = 0;
    this.fromUI = fromUI;
    newTabIndex = this.tabItems.length;
    if (newTabIndex >= this.MAX_TABS_COUNT || this.formlyService.isCollectionOpPending()) return;
    this.formlyService.tabOperationReq.set({ operation: 'AddOperationPoint', collectionKey: String(this.field.key) });
  }

  onTabRemove(tabEvent: { index: number; data: TabItem }) {
    if (tabEvent.index === this.FIRST_TAB_INDEX || this.formlyService.isCollectionOpPending()) return;
    this.formlyService.removedTabIndex = tabEvent.index;
    this.formlyService.tabOperationReq.set({
      operation: 'RemoveOperationPoint',
      collectionKey: String(this.field.key),
      tabIndexToRemove: tabEvent.index,
    });
  }

  /** Deletes the currently active condition tab (triggered from the "Condition" heading's trash icon). */
  deleteActiveTab(): void {
    this.onTabRemove({ index: this.activeIndex, data: this.activeTabItem });
  }

  // Override add() to inject an "id" property into the new tab's model.
  override add(tabResp?: any) {
    super.add();
    this.updateTabItems();
    this.updateActiveTabItem();
    // This logic needs discussion with CalcEngine team
    // if (this.fromUI) {
    //   this.updateField();
    // }
    this.fromUI = false;
  }

  override defaultOptions = {
    className: 'fullWidthComponent',
  };
}
